import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const OPEN_STATUSES = new Set([
  "new", "accepted", "pending_new", "partially_filled",
  "pending_replace", "pending_cancel", "accepted_for_bidding", "held",
]);

function num(v, d = 2) {
  const n = parseFloat(v ?? 0);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function Portfolio() {
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(null);
  const [confirmSymbol, setConfirmSymbol] = useState(null);
  const [canceling, setCanceling] = useState(null);
  const [ordersTab, setOrdersTab] = useState("open"); // "open" | "history"

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/positions"), api.get("/orders?status=all&limit=200")])
      .then(([p, o]) => { setPositions(p.data || []); setOrders(o.data || []); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const closePosition = async (symbol) => {
    setClosing(symbol);
    setConfirmSymbol(null);
    try {
      await api.post(`/positions/${symbol}/close`);
      toast.success(`Close order placed for ${symbol}`);
      setTimeout(load, 1500); // give Alpaca a moment to reflect
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not close ${symbol}`);
    } finally {
      setClosing(null);
    }
  };

  const cancelOrder = async (orderId, symbol) => {
    setCanceling(orderId);
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success(`Cancel requested for ${symbol}`);
      setTimeout(load, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not cancel ${symbol}`);
    } finally {
      setCanceling(null);
    }
  };

  const openOrders = orders.filter((o) => OPEN_STATUSES.has((o.status || "").toLowerCase()));
  const historyOrders = orders.filter((o) => !OPEN_STATUSES.has((o.status || "").toLowerCase()));

  return (
    <div className="p-6 space-y-6" data-testid="portfolio-page">
      <h1 className="text-4xl font-black">Portfolio & History</h1>

      <div className="panel" data-testid="positions-detail">
        <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em] flex items-center justify-between">
          <span>POSITIONS · {positions.length}</span>
          <button onClick={load} data-testid="refresh-portfolio" className="mono text-[10px] text-[#71717A] hover:text-white">REFRESH</button>
        </div>
        {positions.length === 0 ? (
          <div className="p-8 text-center text-[#71717A] mono text-xs">NO OPEN POSITIONS</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                <th className="text-left px-4 py-2 font-normal">SYMBOL</th>
                <th className="text-right px-4 py-2 font-normal">QTY</th>
                <th className="text-right px-4 py-2 font-normal">AVG ENTRY</th>
                <th className="text-right px-4 py-2 font-normal">CURRENT</th>
                <th className="text-right px-4 py-2 font-normal">MKT VAL</th>
                <th className="text-right px-4 py-2 font-normal">P&L</th>
                <th className="text-right px-4 py-2 font-normal w-32">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => {
                const pl = parseFloat(p.unrealized_pl);
                const positive = pl >= 0;
                const isConfirming = confirmSymbol === p.symbol;
                const isClosing = closing === p.symbol;
                return (
                  <tr key={p.symbol} className="border-b border-[#27272A]" data-testid={`portfolio-pos-${p.symbol}`}>
                    <td className="px-4 py-2.5 mono font-bold">{p.symbol}</td>
                    <td className="px-4 py-2.5 mono text-right">{num(p.qty, 0)}</td>
                    <td className="px-4 py-2.5 mono text-right text-[#A1A1AA]">${num(p.avg_entry_price)}</td>
                    <td className="px-4 py-2.5 mono text-right">${num(p.current_price)}</td>
                    <td className="px-4 py-2.5 mono text-right">${num(p.market_value)}</td>
                    <td className={`px-4 py-2.5 mono text-right ${positive ? "gain" : "loss"}`}>
                      {positive ? "+" : ""}${num(p.unrealized_pl)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {isConfirming ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => closePosition(p.symbol)}
                            disabled={isClosing}
                            data-testid={`confirm-close-${p.symbol}`}
                            className="bg-[#FF3B30] text-white px-2.5 py-1 mono text-[10px] tracking-widest font-bold hover:bg-[#E5342B] disabled:opacity-50"
                          >
                            {isClosing ? "CLOSING..." : "CONFIRM"}
                          </button>
                          <button
                            onClick={() => setConfirmSymbol(null)}
                            disabled={isClosing}
                            data-testid={`cancel-close-${p.symbol}`}
                            className="border border-[#27272A] text-[#A1A1AA] px-2.5 py-1 mono text-[10px] tracking-widest hover:border-[#52525B] hover:text-white"
                          >
                            CANCEL
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmSymbol(p.symbol)}
                          data-testid={`close-pos-${p.symbol}`}
                          className="inline-flex items-center gap-1 border border-[#FF3B30]/40 text-[#FF3B30] px-2.5 py-1 mono text-[10px] tracking-widest font-bold hover:bg-[#FF3B30] hover:text-white transition-colors"
                        >
                          <X size={11} /> CLOSE
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel" data-testid="orders-panel">
        <div className="px-4 py-3 border-b border-[#27272A] flex items-center gap-4">
          <button
            onClick={() => setOrdersTab("open")}
            data-testid="orders-tab-open"
            className={`mono text-[11px] tracking-[0.22em] transition-colors ${ordersTab === "open" ? "text-white" : "text-[#71717A] hover:text-[#D4D4D8]"}`}
          >
            OPEN ORDERS · {openOrders.length}
            {ordersTab === "open" && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]" />}
          </button>
          <span className="text-[#3F3F46]">·</span>
          <button
            onClick={() => setOrdersTab("history")}
            data-testid="orders-tab-history"
            className={`mono text-[11px] tracking-[0.22em] transition-colors ${ordersTab === "history" ? "text-white" : "text-[#71717A] hover:text-[#D4D4D8]"}`}
          >
            HISTORY · {historyOrders.length}
          </button>
          <button
            onClick={load}
            data-testid="refresh-orders"
            className="ml-auto mono text-[10px] text-[#71717A] hover:text-white"
          >
            REFRESH
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#71717A] mono text-xs">LOADING...</div>
        ) : ordersTab === "open" ? (
          openOrders.length === 0 ? (
            <div className="p-8 text-center text-[#71717A] mono text-xs" data-testid="no-open-orders">NO OPEN ORDERS</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="open-orders-table">
                <thead>
                  <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                    <th className="text-left px-4 py-2 font-normal">SUBMITTED</th>
                    <th className="text-left px-4 py-2 font-normal">SYMBOL</th>
                    <th className="text-left px-4 py-2 font-normal">SIDE</th>
                    <th className="text-left px-4 py-2 font-normal">TYPE</th>
                    <th className="text-right px-4 py-2 font-normal">QTY</th>
                    <th className="text-right px-4 py-2 font-normal">FILLED</th>
                    <th className="text-right px-4 py-2 font-normal">LIMIT / STOP</th>
                    <th className="text-left px-4 py-2 font-normal">STATUS</th>
                    <th className="text-right px-4 py-2 font-normal w-28">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {openOrders.map((o) => {
                    const isCanceling = canceling === o.id;
                    const limitOrStop = o.limit_price || o.stop_price;
                    return (
                      <tr key={o.id} className="border-b border-[#27272A]" data-testid={`open-order-${o.id}`}>
                        <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">
                          {o.submitted_at ? new Date(o.submitted_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-4 py-2 mono font-bold">{o.symbol}</td>
                        <td className={`px-4 py-2 mono ${o.side === "buy" ? "gain" : "loss"}`}>{o.side?.toUpperCase()}</td>
                        <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">{o.type?.toUpperCase()}</td>
                        <td className="px-4 py-2 mono text-right">{num(o.qty, 0)}</td>
                        <td className="px-4 py-2 mono text-right">{num(o.filled_qty, 0)}</td>
                        <td className="px-4 py-2 mono text-right">{limitOrStop ? `$${num(limitOrStop)}` : "—"}</td>
                        <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">{o.status?.toUpperCase()}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => cancelOrder(o.id, o.symbol)}
                            disabled={isCanceling}
                            data-testid={`cancel-order-${o.id}`}
                            className="inline-flex items-center gap-1 border border-[#FF3B30]/40 text-[#FF3B30] px-2.5 py-1 mono text-[10px] tracking-widest font-bold hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <X size={11} /> {isCanceling ? "CANCELING…" : "CANCEL"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : historyOrders.length === 0 ? (
          <div className="p-8 text-center text-[#71717A] mono text-xs">NO HISTORICAL ORDERS</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-orders-table">
              <thead>
                <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                  <th className="text-left px-4 py-2 font-normal">TIME</th>
                  <th className="text-left px-4 py-2 font-normal">SYMBOL</th>
                  <th className="text-left px-4 py-2 font-normal">SIDE</th>
                  <th className="text-left px-4 py-2 font-normal">TYPE</th>
                  <th className="text-right px-4 py-2 font-normal">QTY</th>
                  <th className="text-right px-4 py-2 font-normal">FILLED</th>
                  <th className="text-right px-4 py-2 font-normal">PRICE</th>
                  <th className="text-left px-4 py-2 font-normal">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {historyOrders.map((o) => (
                  <tr key={o.id} className="border-b border-[#27272A]" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">
                      {o.submitted_at ? new Date(o.submitted_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2 mono font-bold">{o.symbol}</td>
                    <td className={`px-4 py-2 mono ${o.side === "buy" ? "gain" : "loss"}`}>{o.side?.toUpperCase()}</td>
                    <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">{o.type?.toUpperCase()}</td>
                    <td className="px-4 py-2 mono text-right">{num(o.qty, 0)}</td>
                    <td className="px-4 py-2 mono text-right">{num(o.filled_qty, 0)}</td>
                    <td className="px-4 py-2 mono text-right">{o.filled_avg_price ? `$${num(o.filled_avg_price)}` : "—"}</td>
                    <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">{o.status?.toUpperCase()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
